import React, { useState, useCallback, useRef } from 'react';
import { Button, Stack, Tabs, Flex, spacing, useTheme } from '@fti/design-system';
import { UploadZone } from '../components/DatasetImport/UploadZone';
import { UploadProgress } from '../components/DatasetImport/UploadProgress';
import { DataPreview } from '../components/DatasetImport/DataPreview';
import { ImportSummary } from '../components/DatasetImport/ImportSummary';
import { ImportError } from '../components/DatasetImport/ImportError';
import { DataGrid } from '../components/DataGrid';
import {
  startImport,
  uploadChunks,
  cancelImport,
  pollUntilComplete,
  getDataset,
  getColumnTypes,
  confirmReplace,
  detectFileFormat,
  clearActiveImport,
  MAX_FILE_SIZE,
} from '../services/datasetApi';
import type {
  DatasetWithPreview,
  ImportError as ImportErrorType,
  ExistingDatasetConflict,
  ImportSessionStatus,
} from '../types/dataset';
import { extractImportError } from '../types/dataset';

type ImportPhase = 'upload' | 'progress' | 'preview' | 'edit' | 'error';

// TODO: Replace with actual workspace ID from app context
const WORKSPACE_ID = '00000000-0000-0000-0000-000000000001';

export const ImportPage: React.FC = () => {
  const { theme } = useTheme();
  const [phase, setPhase] = useState<ImportPhase>('upload');
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<ImportSessionStatus>('pending');
  const [dataset, setDataset] = useState<DatasetWithPreview | null>(null);
  const [error, setError] = useState<ImportErrorType | null>(null);
  const [conflict, setConflict] = useState<ExistingDatasetConflict | null>(null);
  const [currentFilename, setCurrentFilename] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string | null>(null);

  const runUploadFlow = useCallback(async (sessionId: string, file: File, abortController: AbortController) => {
    await uploadChunks(
      sessionId,
      file,
      (percent) => setProgress(percent),
      abortController.signal
    );

    setStatus('parsing');

    const finalSession = await pollUntilComplete(
      sessionId,
      (percent) => setProgress(percent),
      abortController.signal
    );

    if (finalSession.status === 'failed') {
      const importError = extractImportError(finalSession);
      if (importError) {
        setError(importError);
        setPhase('error');
        return;
      }
    }

    if (finalSession.status === 'cancelled') {
      setPhase('upload');
      return;
    }

    const datasetResult = await getDataset(WORKSPACE_ID);
    if (datasetResult) {
      try {
        const columnTypes = await getColumnTypes(datasetResult.id);
        if (columnTypes) {
          datasetResult.column_types = columnTypes;
        }
      } catch {
        // Column types not yet persisted — inference will handle it
      }
      setDataset(datasetResult);
      setPhase('upload');
    }
  }, []);

  const startUploadPhase = useCallback(() => {
    setPhase('progress');
    setProgress(0);
    setStatus('uploading');
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    return abortController;
  }, []);

  const handleUploadError = useCallback((err: unknown, abortController: AbortController) => {
    if (abortController.signal.aborted) {
      setPhase('upload');
      return;
    }
    if (err && typeof err === 'object' && 'error_type' in err) {
      setError(err as ImportErrorType);
    } else {
      setError({
        error_type: 'parse_failure',
        message: 'An unexpected error occurred during import.',
        guidance: 'Please try again. If the problem persists, check the file format.',
      });
    }
    setPhase('error');
  }, []);

  const handleFileSelected = useCallback(async (file: File) => {
    const format = detectFileFormat(file.name);
    if (!format) {
      setError({
        error_type: 'unsupported_format',
        message: 'Unsupported file format.',
        guidance: 'Please upload a CSV, XLSX, JSON, or parquet file.',
      });
      setPhase('error');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError({
        error_type: 'file_too_large',
        message: `File size (${(file.size / 1024 / 1024 / 1024).toFixed(2)} GB) exceeds the 1 GB limit.`,
        guidance: 'Please reduce the file size or split it into smaller files.',
      });
      setPhase('error');
      return;
    }

    if (file.size === 0) {
      setError({
        error_type: 'empty_file',
        message: 'The uploaded file contains no data.',
        guidance: 'Please select a file that contains data.',
      });
      setPhase('error');
      return;
    }

    setCurrentFilename(file.name);
    setPendingFile(file);
    const abortController = startUploadPhase();

    try {
      await clearActiveImport(WORKSPACE_ID).catch(() => {});

      const result = await startImport(WORKSPACE_ID, file.name, file.size, format);

      if (result.type === 'conflict') {
        // Auto-confirm replacement — user already chose to upload a new file
        const conflictSessionId = result.conflict.confirm_url?.split('/import/')[1]?.split('/')[0];
        if (conflictSessionId) {
          await confirmReplace(conflictSessionId);
          sessionIdRef.current = conflictSessionId;
          await runUploadFlow(conflictSessionId, file, abortController);
          return;
        }
        setConflict(result.conflict);
        setPhase('upload');
        return;
      }

      if (result.type === 'error') {
        setError(result.error);
        setPhase('error');
        return;
      }

      const session = result.session;
      sessionIdRef.current = session.id;
      await runUploadFlow(session.id, file, abortController);
    } catch (err) {
      handleUploadError(err, abortController);
    }
  }, [startUploadPhase, runUploadFlow, handleUploadError]);

  const handleCancel = useCallback(async () => {
    abortControllerRef.current?.abort();
    if (sessionIdRef.current) {
      await cancelImport(sessionIdRef.current);
    }
    setPhase('upload');
    setProgress(0);
  }, []);

  const handleConfirmReplace = useCallback(async () => {
    const file = pendingFile;
    if (!file) return;

    const sessionId = conflict?.confirm_url?.split('/import/')[1]?.split('/')[0];
    setConflict(null);

    if (!sessionId) {
      handleFileSelected(file);
      return;
    }

    await confirmReplace(sessionId);
    sessionIdRef.current = sessionId;
    const abortController = startUploadPhase();

    try {
      await runUploadFlow(sessionId, file, abortController);
    } catch (err) {
      handleUploadError(err, abortController);
    }
  }, [conflict, pendingFile, handleFileSelected, startUploadPhase, runUploadFlow, handleUploadError]);

  const handleDismissError = useCallback(() => {
    setError(null);
    setPhase('upload');
  }, []);

  const handleClearWorkspace = useCallback(async () => {
    try {
      await clearActiveImport(WORKSPACE_ID);
    } catch {
      // Ignore — session may already be gone
    }
    setError(null);
    setPhase('upload');
  }, []);


  const currentTab = phase === 'edit' ? 'edit' : 'import';

  const handleTabChange = useCallback((value: string) => {
    if (value === 'import') {
      setPhase('upload');
    } else if (value === 'edit' && dataset) {
      setPhase('edit');
    }
  }, [dataset]);

  const pageTitle = currentTab === 'edit' ? 'Edit Dataset' : 'Import Dataset';

  return (
    <section
      style={{
        padding: spacing.xl,
        background: theme.colors.background.default,
        color: theme.colors.text.primary,
      }}
    >
      <Stack gap="lg" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tabs.List aria-label="Dataset workflow steps">
            <Tabs.Trigger value="import">1. Import Dataset</Tabs.Trigger>
            <Tabs.Trigger value="edit" disabled={!dataset}>2. Edit Dataset</Tabs.Trigger>
            <Tabs.Trigger value="visualize" disabled>3. Visualize Dataset</Tabs.Trigger>
          </Tabs.List>
        </Tabs>

        <h1
          style={{
            fontSize: '1.5rem',
            fontWeight: 600,
            margin: 0,
            color: theme.colors.text.primary,
          }}
        >
          {pageTitle}
        </h1>

        {phase === 'upload' && (
          <Stack gap="md">
            <UploadZone
              onFileSelected={handleFileSelected}
              conflict={conflict}
              onConfirmReplace={handleConfirmReplace}
              onCancelReplace={() => { setConflict(null); setPendingFile(null); }}
              loadedFilename={dataset ? dataset.filename : undefined}
            />
            {dataset && (
              <>
                <Flex justify="end">
                  <Button variant="solid" color="primary" onClick={() => setPhase('edit')}>
                    Next
                  </Button>
                </Flex>
                <h2
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    margin: 0,
                    color: theme.colors.text.primary,
                  }}
                >
                  Preview
                </h2>
                <DataPreview dataset={dataset} readOnly />
              </>
            )}
          </Stack>
        )}

        {phase === 'progress' && (
          <UploadProgress
            filename={currentFilename}
            progress={progress}
            status={status}
            onCancel={handleCancel}
          />
        )}

        {phase === 'edit' && dataset && (
          <Stack gap="md">
            <ImportSummary dataset={dataset} />
            <DataGrid
              dataset={dataset}
              onSaveComplete={() => {
                // Refresh dataset after save
                getDataset(WORKSPACE_ID).then((refreshed) => {
                  if (refreshed) setDataset(refreshed);
                });
              }}
              toolbarRightContent={
                <Button variant="solid" color="primary" disabled>
                  Next
                </Button>
              }
            />
          </Stack>
        )}

        {phase === 'error' && error && (
          <ImportError
            error={error}
            onDismiss={handleDismissError}
            onClearWorkspace={error.error_type === 'session_active' ? handleClearWorkspace : undefined}
          />
        )}
      </Stack>
    </section>
  );
};
